import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Chip, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  onSnapshot,
  query,
  updateDoc,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { completeMeta } from "../utils/auditFields";
import { propertyCollection, propertyDoc } from "../utils/firestorePaths";

import NewIssueModal from "../components/PropertyIssues/NewIssueModal";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";

const PRIORITY_COLORS = {
  low: { color: "success", label: "Low" },
  medium: { color: "warning", label: "Medium" },
  high: { color: "error", label: "High" },
  urgent: { color: "error", label: "Urgent" },
};

// Custom Filters Component with Property Dropdown
const FacilitiesIssuesFilters = ({ vendors, properties, filter, setFilter }) => {
  const set = (k, v) => setFilter((prev) => ({ ...prev, [k]: v }));

  return (
    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 1 }}>
      <FormControl size="small" sx={{ minWidth: 160 }}>
        <InputLabel id="f-property">Property</InputLabel>
        <Select
          labelId="f-property"
          label="Property"
          value={filter.property}
          onChange={(e) => set("property", e.target.value)}
        >
          <MenuItem value="">All Properties</MenuItem>
          {properties.map((p) => (
            <MenuItem key={p.id} value={p.id}>
              {p.displayName}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: 160 }}>
        <InputLabel id="f-status">Status</InputLabel>
        <Select
          labelId="f-status"
          label="Status"
          value={filter.status}
          onChange={(e) => set("status", e.target.value)}
        >
          <MenuItem value="">All</MenuItem>
          {[
            "not reported",
            "reported",
            "pending estimate",
            "pending approval",
            "pending vendor arrival",
            "work in progress",
            "completed",
          ].map((s) => (
            <MenuItem key={s} value={s}>
              {s}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: 140 }}>
        <InputLabel id="f-priority">Priority</InputLabel>
        <Select
          labelId="f-priority"
          label="Priority"
          value={filter.priority}
          onChange={(e) => set("priority", e.target.value)}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="low">Low</MenuItem>
          <MenuItem value="medium">Medium</MenuItem>
          <MenuItem value="high">High</MenuItem>
          <MenuItem value="urgent">Urgent</MenuItem>
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: 180 }}>
        <InputLabel id="f-vendor">Vendor</InputLabel>
        <Select
          labelId="f-vendor"
          label="Vendor"
          value={filter.vendor}
          onChange={(e) => set("vendor", e.target.value)}
        >
          <MenuItem value="">All</MenuItem>
          {vendors.map((v) => (
            <MenuItem key={v.id} value={v.name}>
              {v.name}
            </MenuItem>
          ))}
          <MenuItem value="unknown">unknown</MenuItem>
          <MenuItem value="TBD">TBD</MenuItem>
        </Select>
      </FormControl>

      <TextField
        size="small"
        label="Search details…"
        value={filter.search}
        onChange={(e) => set("search", e.target.value)}
      />
    </Stack>
  );
};

export default function FacilitiesPropertyIssues({ userInfo }) {
  const [isLoading, setIsLoading] = useState(true);
  const [issues, setIssues] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [filter, setFilter] = useState({
    status: "",
    priority: "",
    vendor: "",
    property: "",
    search: "",
  });
  const nav = useNavigate();

  // Get all properties the user has access to
  const userProperties = userInfo?.properties || [];

  useEffect(() => {
    if (userProperties.length === 0) return;
    setIsLoading(true);

    const allUnsubscribers = [];
    let aggregatedIssues = [];
    let aggregatedVendors = [];

    const setupSubscriptions = async () => {
      for (const property of userProperties) {
        // Subscribe to issues for this property
        const issuesRef = propertyCollection(property.clientId, property.id, "propertyIssues");
        const unsubIssues = onSnapshot(query(issuesRef), (snap) => {
          const propertyIssues = snap.docs
            .map((d) => ({
              id: `${property.id}_${d.id}`, // Unique ID across properties
              originalId: d.id,
              propertyId: property.id,
              clientId: property.clientId,
              propertyName: property.displayName || property.name || property.id,
              ...d.data()
            }))
            .filter((r) => r.completed === false);

          // Remove existing issues from this property and add new ones
          aggregatedIssues = aggregatedIssues.filter(issue => issue.propertyId !== property.id);
          aggregatedIssues = [...aggregatedIssues, ...propertyIssues];
          setIssues([...aggregatedIssues]);
        });

        // Subscribe to vendors for this property
        const vendorsRef = propertyCollection(property.clientId, property.id, "Vendors");
        const unsubVendors = onSnapshot(query(vendorsRef), (snap) => {
          const propertyVendors = snap.docs.map((d) => ({
            id: `${property.id}_${d.id}`, // Unique ID across properties
            originalId: d.id,
            propertyId: property.id,
            propertyName: property.displayName || property.name || property.id,
            ...d.data()
          }));

          // Remove existing vendors from this property and add new ones
          aggregatedVendors = aggregatedVendors.filter(vendor => vendor.propertyId !== property.id);
          aggregatedVendors = [...aggregatedVendors, ...propertyVendors];
          setVendors([...aggregatedVendors]);
        });

        allUnsubscribers.push(unsubIssues, unsubVendors);
      }
      setIsLoading(false);
    };

    setupSubscriptions();

    return () => {
      allUnsubscribers.forEach(unsub => unsub());
    };
  }, [userProperties]);

  const markAsCompleted = async (issueId) => {
    // Extract the original issue ID and property ID
    const issue = issues.find(i => i.id === issueId);
    if (!issue) return;

    const ref = propertyDoc(
      issue.clientId,
      issue.propertyId,
      "propertyIssues",
      issue.originalId
    );
    await updateDoc(ref, { completed: true, ...completeMeta() });
  };

  // Get unique properties for filter dropdown
  const propertyOptions = userProperties.map(p => ({
    id: p.id,
    name: p.displayName || p.name || p.id,
    displayName: p.displayName || p.name || p.id
  }));

  const filtered = issues.filter((it) => {
    if (filter.status && it.status !== filter.status) return false;
    if (filter.priority && (it.priority || "medium") !== filter.priority)
      return false;
    if (filter.vendor && it.vendor !== filter.vendor) return false;
    if (filter.property && it.propertyId !== filter.property) return false;
    if (filter.search) {
      const s = filter.search.toLowerCase();
      const blob = `${it.issueDetails || ""} ${it.status || ""} ${
        it.vendor || ""
      } ${it.propertyName || ""}`.toLowerCase();
      if (!blob.includes(s)) return false;
    }
    return true;
  });

  const columns = [
    {
      field: "propertyName",
      headerName: "Property",
      width: 150,
      sortable: true,
    },
    {
      field: "submittedAt",
      headerName: "Reported",
      width: 140,
      sortable: true,
      renderCell: ({ value }) => {
        if (!value) return "-";

        // Handle Firestore Timestamp objects
        let date;
        if (value && typeof value.toDate === 'function') {
          date = value.toDate();
        } else if (value instanceof Date) {
          date = value;
        } else if (typeof value === 'string') {
          date = new Date(value);
        } else {
          return "-";
        }

        return date.toLocaleDateString() + " " + date.toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit'
        });
      },
    },
    {
      field: "priority",
      headerName: "Priority",
      width: 120,
      renderCell: ({ value }) => {
        const p = PRIORITY_COLORS[value] || PRIORITY_COLORS.medium;
        return <Chip size="small" color={p.color} label={p.label} />;
      },
      sortable: true,
    },
    { field: "status", headerName: "Status", width: 220, sortable: true },
    { field: "vendor", headerName: "Vendor", width: 200, sortable: true },
    { field: "issueDetails", headerName: "Details", flex: 1, minWidth: 240 },
    {
      field: "completed",
      headerName: "Actions",
      width: 160,
      renderCell: (params) => (
        <Button
          onClick={(e) => {
            e.stopPropagation();
            markAsCompleted(params.id);
          }}
        >
          Complete
        </Button>
      ),
      sortable: false,
      filterable: false,
    },
  ];

  return (
    <Box p={2}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={1.5}
      >
        <Typography variant="h6">Property Issues - All Properties ({userProperties.length})</Typography>
        <Button variant="contained" onClick={() => setModalOpen(true)}>
          New Issue
        </Button>
      </Box>

      <FacilitiesIssuesFilters
        vendors={vendors}
        properties={propertyOptions}
        filter={filter}
        setFilter={setFilter}
      />

      <div style={{ height: 560, width: "100%" }}>
        <DataGrid
          rows={filtered}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10, 25, 50]}
          disableSelectionOnClick
          loading={isLoading}
          onRowClick={(params) => nav(`/issues/${params.row.originalId}?property=${params.row.propertyId}&client=${params.row.clientId}`)}
        />
      </div>

      <NewIssueModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        vendors={vendors}
      />
    </Box>
  );
}